<template>
  <section
    class="home-features-bar"
    :class="{ 'home-features-bar--floating': floating }"
    aria-label="Beneficios de compra"
    data-aos="fade"
    data-aos-duration="600"
  >
    <div class="container px-2 px-lg-4">
      <div class="home-features-bar__panel home-glass-surface">
        <div
          v-for="(item, idx) in items"
          :key="item.title"
          class="home-features-bar__item"
          data-aos="fade"
          :data-aos-delay="String(120 + idx * 70)"
        >
          <div class="home-features-bar__icon" aria-hidden="true" v-html="item.icon"></div>
          <div class="home-features-bar__text">
            <p class="home-features-bar__title fw-normal fs-5">{{ item.title }}</p>
            <p class="home-features-bar__subtitle">{{ item.subtitle }}</p>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { defineProps } from 'vue'

defineProps({
  /** Sin fondo de puntos; para superponer al hero (usa el padre para posicionar). */
  floating: {
    type: Boolean,
    default: false,
  },
})

const items = [
  {
    title: 'Envío a domicilio',
    subtitle: 'Gratis en compras seleccionadas',
    icon: `<svg xmlns="http://www.w3.org/2000/svg" width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>`,
  },
  {
    title: 'Garantía',
    subtitle: 'Devoluciones y cambios fáciles',
    icon: `<svg xmlns="http://www.w3.org/2000/svg" width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><path d="m9 12 2 2 4-4"/></svg>`,
  },
  {
    title: 'Soporte al cliente',
    subtitle: 'Atención rápida',
    icon: `<svg xmlns="http://www.w3.org/2000/svg" width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M4 14v-3a8 8 0 1 1 16 0v3"/><path d="M21 18a2 2 0 0 1-2 2h-2v-7h4z"/><path d="M3 18a2 2 0 0 0 2 2h2v-7H3z"/></svg>`,
  },
  {
    title: 'Entrega rápida',
    subtitle: 'Mejor servicio',
    icon: `<svg xmlns="http://www.w3.org/2000/svg" width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M13 2 3 14h7l-1 8 10-12h-7l1-8z"/></svg>`,
  },
]
</script>

<style scoped>
.home-features-bar {
  --features-panel: #0c0c0c;
  --features-border: rgba(255, 255, 255, 0.12);
  --features-divider: rgba(255, 255, 255, 0.12);

  padding: 2rem 0 2.5rem;
  background-color: #000000;
  background-image: radial-gradient(
    circle at center,
    #2a2a2a 1px,
    transparent 1px
  );
  background-size: 14px 14px;
}

/* Solo panel: sin puntos (el padre pone la franja de puntos debajo) */
.home-features-bar--floating {
  padding: 0;
  background: none;
  background-image: none;
}

.home-features-bar__panel {
  display: flex;
  flex-wrap: wrap;
  align-items: stretch;
  border-radius: 16px;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.08);
}

.home-features-bar--floating .home-features-bar__panel {
  box-shadow:
    var(--home-glass-shadow, 0 0 1px rgba(255, 255, 255, 0.08)),
    0 20px 48px rgba(0, 0, 0, 0.55),
    0 0 0 1px rgba(0, 0, 0, 0.35);
}

.home-features-bar :deep(.container) {
  padding-left: max(0.5rem, env(safe-area-inset-left, 0px));
  padding-right: max(0.5rem, env(safe-area-inset-right, 0px));
}

.home-features-bar__item {
  flex: 1 1 0;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: clamp(1rem, 2.5vw, 1.35rem) clamp(1rem, 2.5vw, 1.5rem);
  min-height: 7.5rem;
  height: auto;
  border-right: 1px solid var(--features-divider);
}

.home-features-bar__item:last-child {
  border-right: none;
}

.home-features-bar__icon {
  flex-shrink: 0;
  width: 3.125rem;
  height: 3.125rem;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(220, 38, 38, 0.96);
  background: linear-gradient(145deg, rgba(220, 38, 38, 0.22) 0%, rgba(16, 16, 16, 0.95) 70%);
  box-shadow:
    inset 0 0 0 1px rgba(255, 255, 255, 0.1),
    0 6px 18px rgba(0, 0, 0, 0.38);
  transition:
    transform 0.22s ease,
    box-shadow 0.22s ease,
    color 0.22s ease;
}

.home-features-bar__item:hover .home-features-bar__icon {
  transform: translateY(-2px);
  color: rgb(251, 113, 133);
  box-shadow:
    inset 0 0 0 1px rgba(252, 165, 165, 0.45),
    0 12px 28px rgba(0, 0, 0, 0.42);
}

.home-features-bar__icon :deep(svg) {
  display: block;
  width: 1.45rem;
  height: 1.45rem;
}

.home-features-bar__text {
  min-width: 0;
}

.home-features-bar__title {
  margin: 0;
  font-size: 0.95rem;
  font-weight: 700;
  color: #ffffff;
  line-height: 1.25;
  letter-spacing: 0.01em;
}

.home-features-bar__subtitle {
  margin: 0.2rem 0 0;
  font-size: 0.8rem;
  font-weight: 400;
  color: rgba(255, 255, 255, 0.65);
  line-height: 1.35;
}

@media (max-width: 991.98px) {
  .home-features-bar__item {
    flex: 1 1 50%;
    border-right: 1px solid var(--features-divider);
    border-bottom: 1px solid var(--features-divider);
  }

  .home-features-bar__item:nth-child(2n) {
    border-right: none;
  }

  .home-features-bar__item:nth-last-child(-n + 2) {
    border-bottom: none;
  }
}

@media (max-width: 575.98px) {
  .home-features-bar :deep(.container) {
    padding-left: max(0px, env(safe-area-inset-left, 0px));
    padding-right: max(0px, env(safe-area-inset-right, 0px));
  }

  .home-features-bar__panel {
    margin: 0;
  }

  .home-features-bar:not(.home-features-bar--floating) {
    padding: 1.25rem 0 max(1.75rem, env(safe-area-inset-bottom, 0px));
  }

  .home-features-bar__item {
    flex: 1 1 100%;
    border-right: none;
    border-bottom: 1px solid var(--features-divider);
    padding: 1.1rem 1.15rem;
    min-height: 0;
  }

  .home-features-bar__item:last-child {
    border-bottom: none;
  }
}
</style>
